import { Card, CardContent } from '@/ui/base/card';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { cn } from '@/nucleo/utilitarios';

export type TomEtapaPipeline = 'pendente' | 'ativo' | 'concluido';

export interface EtapaCicloFinanceiro {
  chave: string;
  rotulo: string;
  tom: TomEtapaPipeline;
  qtd: number;
  valor: number;
}

export interface CicloFinanceiroPipelineDados {
  docsACadastrar: number;
  docsCadastrados: number;
  etapas: EtapaCicloFinanceiro[];
}

const PONTO_TOM: Record<TomEtapaPipeline, string> = {
  pendente: 'bg-warning',
  ativo: 'bg-primary',
  concluido: 'bg-success',
};
const TEXTO_TOM: Record<TomEtapaPipeline, string> = {
  pendente: 'text-warning',
  ativo: 'text-primary',
  concluido: 'text-success',
};
const TRILHA_TOM: Record<TomEtapaPipeline, string> = {
  pendente: 'bg-warning/70',
  ativo: 'bg-primary/70',
  concluido: 'bg-success/70',
};

/** Pipeline visual do ciclo orçamentário-financeiro: do empenho à
 * autorização de pagamento — mesma estrutura do `PipelineFinanceiro` já
 * portado em `operacao/financeiro/painel-executivo`, cópia local pra não
 * criar acoplamento entre módulos. */
export function PipelineFinanceiro({
  dados,
  titulo = 'Ciclo orçamentário-financeiro',
}: {
  dados: CicloFinanceiroPipelineDados;
  titulo?: string;
}) {
  const maiorValor = Math.max(...dados.etapas.map((e) => e.valor), 1);

  return (
    <Card className="rounded-2xl">
      <CardContent className="space-y-4 pb-5 pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-0.5">
            <p className="text-sm font-semibold leading-none">{titulo}</p>
            <p className="text-xs text-muted-foreground">
              Fluxo das medições — do empenho à autorização de pagamento
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="rounded-md border bg-warning/5 px-2.5 py-1 text-[11px]">
              <span className="text-muted-foreground">Docs a cadastrar</span>
              <span className="ml-1.5 font-semibold tabular-nums text-warning">
                {dados.docsACadastrar}
              </span>
            </div>
            <div className="rounded-md border bg-success/5 px-2.5 py-1 text-[11px]">
              <span className="text-muted-foreground">Cadastrados</span>
              <span className="ml-1.5 font-semibold tabular-nums text-success">
                {dados.docsCadastrados}
              </span>
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto">
          <div className="hidden min-w-[640px] md:block">
            <div className="relative">
              <div
                className="absolute left-0 right-0 top-[14px] h-px bg-border"
                aria-hidden="true"
              />
              <div className="relative grid grid-cols-7 gap-x-2 gap-y-4">
                {dados.etapas.map((e) => (
                  <EtapaPipeline key={e.chave} etapa={e} maiorValor={maiorValor} comMarcador />
                ))}
              </div>
            </div>
          </div>
          <div className="grid min-w-[320px] grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-4 md:hidden">
            {dados.etapas.map((e) => (
              <EtapaPipeline key={e.chave} etapa={e} maiorValor={maiorValor} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t pt-1 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-warning" aria-hidden="true" /> Pendente
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" /> Em andamento
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-success" aria-hidden="true" /> Concluído
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function EtapaPipeline({
  etapa,
  maiorValor,
  comMarcador,
}: {
  etapa: EtapaCicloFinanceiro;
  maiorValor: number;
  comMarcador?: boolean;
}) {
  const pct = Math.round((etapa.valor / maiorValor) * 100);
  return (
    <div className="flex min-w-0 flex-col items-stretch">
      {comMarcador && (
        <div className="mb-2 flex justify-center">
          <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-background bg-card ring-1 ring-border">
            <span
              className={cn('h-2.5 w-2.5 rounded-full', PONTO_TOM[etapa.tom])}
              aria-hidden="true"
            />
          </span>
        </div>
      )}
      <div className="min-w-0 space-y-1 px-1 text-center">
        <p
          className={cn('text-[10px] font-semibold uppercase tracking-wider', TEXTO_TOM[etapa.tom])}
        >
          {etapa.rotulo}
        </p>
        <p className="truncate text-[15px] font-bold leading-tight tabular-nums">
          <MoneyDisplay value={etapa.valor} />
        </p>
        <p className="text-[10px] tabular-nums text-muted-foreground">
          {etapa.qtd} {etapa.qtd === 1 ? 'medição' : 'medições'}
        </p>
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full', TRILHA_TOM[etapa.tom])}
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
      </div>
    </div>
  );
}
